import { IdToken } from '@auth0/auth0-angular';
import { FingerprintJSPro } from '@fingerprintjs/fingerprintjs-pro-angular';

import { Component, OnDestroy, OnInit } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { take } from 'rxjs/operators';

import { MijnAuthSessieManagerService } from '@ap/mijn-auth-sessie-manager';
import { ApContentService } from '@ap/content';

import content from '../login/login.wrapper.content.json';

import { FingerprintService } from './fingerprint/fingerprint.service';

/**
 * @ngdoc component
 * @name Auth0loginWrapperComponent
 * @module LoginWrapperModule
 */
@Component({
    selector: 'auth0inloggen',
    templateUrl: './auth0login.wrapper.component.html',
    standalone: false
})
class Auth0loginWrapperComponent implements OnInit, OnDestroy {
    public visitorData: FingerprintJSPro.ExtendedGetResult | FingerprintJSPro.GetResult | null = null;
    public fingerprintId: string;
    public confidence: number;

    public accessToken: string;
    public claimExp: string;
    public sensitiveAction: string;
    public authenticated = false;
    public rawIdToken: string;
    private auth0Listeners$ = new Subject<void>();

    public constructor(
        private readonly activatedRoute: ActivatedRoute,
        public readonly auth: MijnAuthSessieManagerService,
        private readonly contentService: ApContentService,
        private readonly fingerprintService: FingerprintService
    ) {}

    public ngOnInit(): void {
        this.contentService.addContentItems(content);

        this.auth0isAuthenticated()
            .pipe(take(1))
            .subscribe((result) => {
                if (result) {
                    this.getTokenData();
                    this.startSessionMonitoring(result);
                    this.auth.idTokenClaims$.subscribe((idToken) => {
                        this.rawIdToken = idToken?.__raw;
                    });
                }
            });
    }

    public ngOnDestroy() {
        this.auth0Listeners$.next();
        this.auth0Listeners$.complete();
    }

    public auth0isAuthenticated(): Observable<boolean> {
        return this.auth.isAuthenticated$;
    }

    public emailPasskeyLogin() {
        this.auth.login().subscribe();
    }

    public otpEmailLogin() {
        this.auth.loginWithEmail().subscribe();
    }

    public emailSignupRedirect() {
        window.location.href = '/online/web/onlinebankieren/nieuwe-login-aanmelden';
    }

    public auth0logout() {
        this.auth.logout().subscribe();
    }

    public parseTokenTimes(payload: IdToken) {
        const authTime = new Date(payload.auth_time + 0 * 1000);
        const issuedAt = new Date(payload.iat * 1000);
        const expiresAt = new Date(payload.exp * 1000);

        const tokenLifetimeMinutes = (payload.exp - payload.iat) / 60;

        return {
            authTime,
            issuedAt,
            expiresAt,
            lifetimeMinutes: tokenLifetimeMinutes
        };
    }

    public startSessionMonitoring(isAuthenticated: boolean) {
        if (isAuthenticated) {
            setInterval(() => this.getTokenData(), 5000); // Check elke 5 sec
        }
    }

    private getTokenData() {
        this.auth.getAccessTokenSilently(true).subscribe({
            next: (token: any) => {
                const payload = JSON.parse(atob(token.access_token.split('.')[1]));

                let milliSeconds = payload.exp * 1000 - Date.now();
                this.accessToken = this.convertMsToTimestamp(milliSeconds);

                const idPayload = JSON.parse(atob(token.id_token.split('.')[1]));

                milliSeconds = idPayload.exp * 1000 - Date.now();
                this.claimExp = milliSeconds > 0 ? this.convertMsToTimestamp(milliSeconds) : 'expired';
            }
        });
        this.auth0isAuthenticated().subscribe((authenticated) => (this.authenticated = authenticated));
    }

    private convertMsToTimestamp(milliseconds: number): string {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        return [hours, minutes, seconds].map((val) => val.toString().padStart(2, '0')).join(':');
    }
    private getRedirectUrl() {
        const origin = window.location.origin;
        const loginLink =
            origin.indexOf('localhost') > -1
                ? '/mijn-login/auth0inloggen'
                : this.contentService.get('mijn.login.login.wrapper.mijn-omgeving.link');
        return `${origin}${loginLink}`;
    }

    private getLoginUrl() {
        const origin = window.location.origin;
        const loginLink =
            origin.indexOf('localhost') > -1
                ? '/mijn-login/auth0inloggen'
                : `${this.contentService.get('mijn.login.login.wrapper.mijn-omgeving.link')}`;
        return `${origin}${loginLink}`;
    }

    private parseJwt(token: string) {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => {
                    return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
                })
                .join('')
        );
        return JSON.parse(jsonPayload);
    }
}

export default Auth0loginWrapperComponent;
